<template>
  <div :class="wrapperClass">
    <component
      :is="useAnchor ? 'a' : 'button'"
      :id="`cta-button-${id}`"
      :class="[
        styles,
        Array.isArray(buttonClass) ? buttonClass.join(' ') : buttonClass,
        { disabled: isDisabled }
      ]"
      :disabled="isDisabled && !useAnchor"
      :aria-disabled="isDisabled || isLoading ? 'true' : 'false'"
      :aria-busy="isLoading ? 'true' : 'false'"
      :title="text"
      :aria-label="text"
      :tabindex="isDisabled || isLoading ? '-1' : '0'"
      :aria-describedby="descriptionId"
      :aria-haspopup="hasPopUp ? hasPopUp : undefined"
      :href="useAnchor ? href : undefined"
      :target="useAnchor ? target : undefined"
      :download="
        useAnchor && shouldDownload
          ? typeof shouldDownload === 'string'
            ? shouldDownload
            : ''
          : undefined
      "
      :rel="
        useAnchor && target === '_blank' ? 'noopener noreferrer' : undefined
      "
      :type="!useAnchor ? type : undefined">
      <AtomSpinner v-if="isLoading" :size="18" />
      <slot v-else-if="slots.text" name="text"></slot>
      <span v-else>{{ actionText }}</span>
    </component>
    <div :id="descriptionId" class="visually-hidden">{{ actionText }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useSlots, type PropType } from 'vue';

const slots = useSlots();

const props = defineProps({
  id: {
    type: String as PropType<string>,
    default: ''
  },
  appearance: {
    type: String as PropType<'primary' | 'secondary' | 'tertiary' | 'unstyled'>,
    default: 'primary'
  },
  width: {
    type: String as PropType<'full' | 'content'>,
    default: 'content'
  },
  rounded: {
    type: String as PropType<'full' | 'md' | 'none'>,
    default: 'full'
  },
  isDisabled: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  isLoading: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  actionText: {
    type: String as PropType<string>,
    default: ''
  },
  buttonClass: {
    type: [String, Array] as PropType<string | string[]>,
    default: ''
  },
  hasPopUp: {
    type: [String, Boolean] as PropType<
      'dialog' | 'menu' | 'listbox' | 'tree' | 'grid' | undefined | boolean
    >,
    default: undefined
  },
  type: {
    type: String as PropType<'button' | 'submit' | 'reset'>,
    default: 'button'
  },
  useAnchor: {
    type: Boolean,
    default: false
  },
  href: {
    type: String as PropType<string>,
    default: ''
  },
  target: {
    type: String as PropType<string>,
    default: ''
  },
  shouldDownload: {
    type: [String, Boolean] as PropType<string | boolean>,
    default: false
  },
  alignment: {
    type: String as PropType<'left' | 'right' | 'center'>,
    default: 'center'
  }
});

const wrapperClass = computed(() => {
  const baseClass = props.useAnchor ? 'inline-block-wrapper' : 'button-wrapper';
  let alignmentClass = '';

  if (!props.useAnchor) {
    alignmentClass = (() => {
      switch (props.alignment) {
        case 'left':
        case 'right':
          return 'align-right';
        case 'center':
        default:
          return 'align-center';
      }
    })();
  }

  return `${baseClass} ${alignmentClass}`.trim();
});

const styles = computed(() => {
  let s = 'button';

  if (props.appearance === 'primary') s += ' button-primary';
  else if (props.appearance === 'secondary') s += ' button-secondary';
  else if (props.appearance === 'tertiary') s += ' button-tertiary';
  else if (props.appearance === 'unstyled') s = ' button-unstyled';

  if (props.width === 'full') s += ' w-full';

  if (props.rounded === 'full') s += ' rounded-full';
  else if (props.rounded === 'md') s += ' rounded';

  if (props.isDisabled) s += ' disabled';
  return s;
});

const text = computed(() => props.actionText || 'Button');
const descriptionId = ref(`description af ${props.actionText}`);
</script>

<style lang="scss" scoped>
.visually-hidden {
  position: absolute !important;
  clip: rect(1px, 1px, 1px, 1px) !important;
  padding: 0 !important;
  border: 0 !important;
  height: 1px !important;
  width: 1px !important;
  overflow: hidden !important;
}

.button-wrapper {
  width: 100%;
  display: flex;
  box-sizing: border-box;
  align-items: center;
}

.inline-block-wrapper {
  width: fit-content;
  display: inline-block;
}

.align-left {
  justify-content: flex-start;
}

.align-right {
  justify-content: flex-end;
}

.align-center {
  justify-content: center;
}

// ── Button base ──
.button {
  appearance: none;
  display: inline-block;
  width: fit-content;
  padding: units(3) calc(#{units(5)} - #{$button-border-width});
  border-radius: 0px;
  border-style: solid;
  border-color: color($button-primary-default-border-color);
  font-size: font-size('xs');
  font-weight: font-weight('bold');
  color: color($button-primary-default-color);
  @include font-smoothing;
  line-height: units($button-line-height, 'rem');
  text-align: center;
  justify-content: center;
  text-decoration: none;
  white-space: nowrap;
  -webkit-user-select: none;
  user-select: text;
  cursor: pointer;
  outline: 1px solid transparent;
  transition: all 0.2s ease;

  @include media-breakpoint-up(sm) {
    width: auto;
  }

  &.focus,
  &:focus {
    @include focus();
    color: color($button-primary-focus-color);
  }

  &:hover,
  &.button-hover {
    text-decoration: none;
  }

  // ── Primary ──
  &.button-primary {
    color: color($button-primary-default-color);
    background-color: color($button-primary-default-background-color);
    border-color: color($button-primary-default-border-color);

    &:hover,
    &.button-hover {
      background-color: color($button-primary-hover-background-color);
      border-color: color($button-primary-hover-border-color);
      text-decoration: none;
    }

    &.focus,
    &:focus {
      background-color: color($button-primary-focus-background-color);
      border-color: color($button-primary-focus-border-color);
    }

    &:active,
    &.button-active {
      background-color: color($button-primary-active-background-color);
      border-color: color($button-primary-active-border-color);
    }

    &:disabled,
    &.disabled {
      opacity: $button-disabled-opacity;
      box-shadow: none !important;
      cursor: not-allowed;

      &:hover {
        color: color($button-primary-hover-color) !important;
        background-color: color(
          $button-primary-default-background-color
        ) !important;
        border-color: color($button-primary-default-border-color) !important;
        opacity: $button-disabled-opacity !important;
        box-shadow: none !important;
      }
    }
  }

  // ── Secondary ──
  &.button-secondary {
    color: color($button-secondary-default-color);
    background-color: color($button-secondary-default-background-color);
    border-color: color($button-secondary-default-border-color);

    &:hover,
    &.button-hover {
      color: color($button-secondary-hover-color);
      background-color: color($button-secondary-hover-background-color);
      border-color: color($button-secondary-hover-border-color);
    }

    &.focus,
    &:focus {
      color: color($button-secondary-focus-color);
      background-color: color($button-secondary-focus-background-color);
      border-color: color($button-secondary-focus-border-color);
    }

    &:active,
    &.button-active {
      color: color($button-secondary-active-color);
      background-color: color($button-secondary-active-background-color);
      border-color: color($button-secondary-active-border-color);
    }

    &:disabled,
    &.disabled {
      opacity: $button-disabled-opacity;
      box-shadow: none !important;
      cursor: not-allowed;
      border-color: color($button-secondary-disabled-border-color) !important;

      &:hover {
        color: color($button-secondary-default-color) !important;
        background-color: color(
          $button-secondary-default-background-color
        ) !important;
        border-color: color($button-secondary-disabled-border-color) !important;
        opacity: $button-disabled-opacity !important;
        box-shadow: none !important;
      }
    }
  }

  // ── Tertiary ──
  &.button-tertiary {
    border-color: color($button-tertiary-default-border-color);
    background-color: color($button-tertiary-default-background-color);
    color: color($button-tertiary-default-color);
    font-weight: font-weight('normal');

    &:hover,
    &.button-hover {
      border-color: color($button-tertiary-hover-border-color);
      background-color: color($button-tertiary-hover-background-color);
      color: color($button-tertiary-hover-color);
    }

    &.focus,
    &:focus {
      border-color: color($button-tertiary-focus-border-color);
      background-color: color($button-tertiary-focus-background-color);
      color: color($button-tertiary-focus-color);
    }

    &:active,
    &.button-active {
      border-color: color($button-tertiary-active-border-color);
      background-color: color($button-tertiary-active-background-color);
      color: color($button-tertiary-active-color);
    }

    &:disabled,
    &.disabled {
      opacity: $button-disabled-opacity;
      box-shadow: none !important;
      cursor: not-allowed;

      &:hover {
        border-color: color(
          $button-tertiary-default-background-color
        ) !important;
        background-color: color(
          $button-tertiary-default-background-color
        ) !important;
        color: color($button-tertiary-default-color);
        font-weight: font-weight('normal') !important;
        opacity: $button-disabled-opacity;
        box-shadow: none !important;
      }
    }
  }

  // ── Quaternary (link-style) ──
  &.button-quaternary {
    color: color($button-quaternary-default-color);
    background-color: color($button-quaternary-default-background-color);
    border-color: transparent;
    text-decoration: none;
    font-weight: font-weight('normal');
    padding-left: calc(8px - #{$button-border-width});

    &:hover,
    &.button-hover {
      background-color: color($button-quaternary-hover-background-color);
      box-shadow: none;
      border-bottom-color: transparent;
    }

    &.focus,
    &:focus {
      color: color($button-quaternary-default-color);
      box-shadow: none;
      border-bottom-color: transparent;
      background-color: color($button-quaternary-focus-background-color);
    }

    &:active,
    &.button-active {
      background-color: color($button-quaternary-active-background-color);
      box-shadow: none;
      border-bottom-color: transparent;
    }

    &:disabled,
    &.disabled {
      opacity: $button-disabled-opacity;
      box-shadow: none !important;
      cursor: not-allowed;

      &:hover {
        color: color($button-quaternary-default-color) !important;
        background-color: color(
          $button-quaternary-default-background-color
        ) !important;
        font-weight: font-weight('normal') !important;
        opacity: $button-disabled-opacity !important;
        box-shadow: none !important;
      }
    }

    .icon-svg {
      margin-right: 0;
      margin-bottom: -2px;
      margin-top: -4px;
    }
  }

  &.button-small {
    padding: calc(#{units(1)}) calc(#{units(4)} - #{$button-border-width}) 0px;
    font-size: font-size($typography-small-text-font-size);
    min-height: 24px;
  }

  .icon-svg {
    margin-right: 4px;
    fill: currentColor;
    margin-top: -0.2em;
  }
}

// ── Unstyled ──
.button-unstyled {
  background-color: transparent;
  border: 0;
  border-radius: 0;
  margin: 0;
  padding: 0;
  text-align: left;
  -webkit-font-smoothing: auto;
  color: inherit;
  min-height: auto;
  font-weight: font-weight('normal');

  &:hover,
  &:focus {
    background-color: inherit;
    color: inherit;
  }

  &:disabled,
  &.disabled {
    opacity: $button-disabled-opacity;
    box-shadow: none !important;
    cursor: not-allowed;

    &:hover {
      color: color($button-quaternary-default-color) !important;
      background-color: color(
        $button-quaternary-default-background-color
      ) !important;
      font-weight: font-weight('normal') !important;
      opacity: $button-disabled-opacity !important;
      box-shadow: none !important;
    }
  }
}

.w-full {
  width: 100%;
}

.rounded-full {
  border-radius: $button-border-radius !important;
}

.rounded {
  border-radius: $button-border-radius-small;
}
</style>
